/**

 * Unified loader for all splat file formats using splat-transform.

 */



import {

    getInputFormat,

    readFile,

    combine,

    sortMortonOrder,

    Column,

    ColumnType,

    DataTable,

    Options,

    ReadFileSystem,

    ProgressCallback,

    ZipReadFileSystem

} from '@playcanvas/splat-transform';

import { GSplatData } from 'playcanvas';



/**

 * Default options for readFile.

 */

const defaultOptions: Options = {

    iterations: 10,

    lodSelect: [0],

    unbundled: false,

    lodChunkCount: 512,

    lodChunkExtent: 16

};



const sameLodSelect = (a: number[], b: number[]) => {

    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {

        if (a[i] !== b[i]) return false;

    }

    return true;

};



const fireLoadStage = (stage: string, detail: Record<string, unknown> = {}) => {

    try {

        window.dispatchEvent(new CustomEvent('volugraph-load-stage', { detail: { stage, ...detail } }));

    } catch {

        /* ignore */

    }

};



const resolveInputFormat = (filename: string) => {

    const lower = filename.toLowerCase();

    if (lower.endsWith('lod-meta.json')) {

        return 'lod-meta';

    }

    return getInputFormat(filename);

};



type LodMetaJson = {

    lodLevels?: number;

    filenames?: string[];

    volugraphAdditiveBands?: boolean;

};



export type LodPartialReadyCallback = (

    table: DataTable,

    detail: { band: number; bandCount: number; rows: number; isFinal: boolean }

) => void | Promise<void>;



const extractLodLevelsFromFilenames = (filenames: string[]): number[] => {

    const levels = new Set<number>();

    for (const fn of filenames) {

        const match = /^(\d+)_\d+\/meta\.json$/i.exec(fn);

        if (match) levels.add(parseInt(match[1], 10));

    }

    return [...levels].sort((a, b) => a - b);

};



const chunkFilesForLod = (filenames: string[], lod: number): string[] =>

    filenames.filter((fn) => {

        const match = /^(\d+)_\d+\/meta\.json$/i.exec(fn);

        return match && parseInt(match[1], 10) === lod;

    });



/**
 * Streamed SOG bundles load via native PlayCanvas gsplat (see volugraph-stream-lod.ts).
 * splat-transform band merging was removed — it loaded everything and crashed mobile.
 */
const readLodMetaBundle = async (
    _filename: string,
    _fileSystem: ReadFileSystem,
    _options: Options,
    _onPartialReady?: LodPartialReadyCallback
): Promise<DataTable[]> => {
    throw new Error(
        'lod-meta.json requires native stream LOD (enable REACT_APP_VOLUGRAPH_STREAM_LOD / volugraphViewerStreamLod)'
    );
};



/**

 * Volugraph embed may request progressive additive bands or a lighter LOD for first paint.

 * Fallback to default [0] happens below if requested LOD is unavailable in the source file.

 */

const getRuntimeReadOptions = (): Options => {

    const cfg = (window as unknown as {

        __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: {

            volugraphViewerLodSelect?: number[];

            volugraphViewerLodProgressive?: boolean;

        };

    }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__;

    if (cfg?.volugraphViewerLodProgressive === true) {

        return defaultOptions;

    }

    const requested = cfg?.volugraphViewerLodSelect;

    if (!Array.isArray(requested) || requested.length === 0) {

        return defaultOptions;

    }

    const lodSelect = requested

        .map((n) => Number(n))

        .filter((n) => Number.isInteger(n) && n >= 0)

        .slice(0, 8) as number[];

    if (lodSelect.length === 0) {

        return defaultOptions;

    }

    return {

        ...defaultOptions,

        lodSelect

    };

};



export const isVolugraphProgressiveLodLoad = (): boolean => false;



/**

 * Map splat-transform column types to GSplatData property types.

 */

const columnTypeToGSplatType = (colType: ColumnType | null): string => {

    switch (colType) {

        case 'int8': return 'char';

        case 'uint8': return 'uchar';

        case 'int16': return 'short';

        case 'uint16': return 'ushort';

        case 'int32': return 'int';

        case 'uint32': return 'uint';

        case 'float32': return 'float';

        case 'float64': return 'double';

        default: return 'float';

    }

};



/**

 * Convert a splat-transform DataTable to PlayCanvas GSplatData.

 */

const dataTableToGSplatData = (dataTable: DataTable): GSplatData => {

    const properties = dataTable.columns.map((col: Column) => ({

        type: columnTypeToGSplatType(col.dataType),

        name: col.name,

        storage: col.data,

        byteSize: col.data.BYTES_PER_ELEMENT

    }));



    const gsplatData = new GSplatData([{

        name: 'vertex',

        count: dataTable.numRows,

        properties

    }]);



    // Support loading 2D splats by adding scale_2 property with almost 0 scale

    if (gsplatData.getProp('scale_0') && gsplatData.getProp('scale_1') && !gsplatData.getProp('scale_2')) {

        const scale2 = new Float32Array(gsplatData.numSplats).fill(Math.log(1e-6));

        gsplatData.addProp('scale_2', scale2);



        // Place the new scale_2 property just after scale_1

        const props = gsplatData.getElement('vertex').properties;

        props.splice(props.findIndex((prop: any) => prop.name === 'scale_1') + 1, 0, props.splice(props.length - 1, 1)[0]);

    }



    return gsplatData;

};



/**

 * Load a file using splat-transform and convert to GSplatData.

 * @param filename - The filename to load

 * @param fileSystem - The file system to read from

 * @param skipReorder - Skip morton reordering (for files already in morton order or animation playback)

 * @param onProgress - Optional download progress callback (bytes loaded / total)

 * @param onPartialReady - Optional callback after each additive LOD band (progressive stream bundles)

 */

const loadGSplatData = async (

    filename: string,

    fileSystem: ReadFileSystem,

    skipReorder?: boolean,

    onProgress?: ProgressCallback,

    onPartialReady?: LodPartialReadyCallback

): Promise<GSplatData> => {

    const inputFormat = resolveInputFormat(filename);

    const lowerFilename = filename.toLowerCase();

    const runtimeOptions = getRuntimeReadOptions();

    const canFallbackToDefault = !sameLodSelect(runtimeOptions.lodSelect ?? [], defaultOptions.lodSelect ?? [])

        && !isVolugraphProgressiveLodLoad();



    fireLoadStage('embed.download.start', { filename: lowerFilename.split('/').pop() });



    const progressFs: ReadFileSystem = onProgress ? {

        createSource: (name, inner) => fileSystem.createSource(name, (loaded, total) => {

            inner?.(loaded, total);

            onProgress(loaded, total);

        })

    } : fileSystem;



    const readTables = async (options: Options) => {

        if (inputFormat === 'lod-meta') {

            return readLodMetaBundle(filename, progressFs, options, onPartialReady);

        }

        // Handle bundled SOG (.sog extension) - wrap with ZipReadFileSystem

        if (inputFormat === 'sog' && lowerFilename.endsWith('.sog')) {

            const source = await progressFs.createSource(filename);

            const zipFs = new ZipReadFileSystem(source);

            try {

                return await readFile({

                    filename: 'meta.json',

                    inputFormat: 'sog',

                    options,

                    params: [],

                    fileSystem: zipFs

                });

            } finally {

                zipFs.close();

            }

        }

        return await readFile({

            filename,

            inputFormat,

            options,

            params: [],

            fileSystem: progressFs

        });

    };



    let tables;

    try {

        tables = await readTables(runtimeOptions);

    } catch (err) {

        if (!canFallbackToDefault) {

            throw err;

        }

        // Requested viewer LOD may not exist in this file; retry with legacy default.

        if (typeof console !== 'undefined' && console.warn) {

            console.warn('[Volugraph] LOD read fallback -> default [0]', err);

        }

        tables = await readTables(defaultOptions);

    }



    // If runtime LOD returns no table, fallback once to default [0].

    if ((!tables || tables.length === 0 || !tables[0]) && canFallbackToDefault) {

        try {

            tables = await readTables(defaultOptions);

        } catch {

            /* preserve downstream no-table error */

        }

    }

    if (!tables || !tables[0]) {

        throw new Error('readFile returned no splat tables');

    }



    if (onProgress) {

        try {

            window.dispatchEvent(new CustomEvent('volugraph-splat-download-complete'));

        } catch {

            /* ignore */

        }

    }

    fireLoadStage('embed.download.complete');

    fireLoadStage('embed.parse.complete', { rows: tables[0]?.numRows ?? 0 });



    // Reorder data into morton order for better render performance.

    // Skip reordering for:

    // - SOG format (already in morton order)

    // - Compressed PLY (already in morton order from write-compressed-ply)

    // - Streamed SOG / lod-meta chunks (already ordered at encode)

    // - When skipReorder is true (ssproj files are already ordered, animation frames need speed)

    const isCompressedPly = lowerFilename.endsWith('.compressed.ply');

    if (inputFormat !== 'sog' && inputFormat !== 'lod-meta' && !isCompressedPly && !skipReorder) {

        const indices = new Uint32Array(tables[0].numRows);

        for (let i = 0; i < indices.length; i++) {

            indices[i] = i;

        }

        sortMortonOrder(tables[0], indices);

        tables[0].permuteRowsInPlace(indices);

    }



    // Convert to GSplatData (use first table, as most formats return single table)

    // LCC may return multiple tables for different LOD levels - we use the first (highest detail)

    return dataTableToGSplatData(tables[0]);

};



/**

 * Validate that GSplatData contains required properties.

 */

const validateGSplatData = (gsplatData: GSplatData): void => {

    const required = [

        'x', 'y', 'z',

        'scale_0', 'scale_1', 'scale_2',

        'rot_0', 'rot_1', 'rot_2', 'rot_3',

        'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity'

    ];



    const missing = required.filter(x => !gsplatData.getProp(x));

    if (missing.length > 0) {

        throw new Error(`This file does not contain gaussian splatting data. The following properties are missing: ${missing.join(', ')}`);

    }

};



export {

    loadGSplatData,

    validateGSplatData,

    dataTableToGSplatData

};


